/**
 * useConversationPersistence — Chat-Verläufe in `icarus_conversations`.
 *
 * Vorher lag diese Logik als privates `saveConversation` in
 * `useNutriChat`. Der globale Assistent (`useIcarusChat`) hatte sie gar
 * nicht: sein Verlauf lebte rein im State und war nach jedem Reload weg
 * (dokumentiert als offene Lücke in `HANDOVER.md` §4, „Chat-Persistenz").
 * Statt sie ein zweites Mal zu schreiben, liegt sie jetzt hier — beide
 * Oberflächen schreiben in dieselbe Tabelle, mit demselben Verhalten.
 *
 * Zwei Dinge, die die alte Inline-Variante nicht konnte:
 *
 *   1. **Kein Doppel-Insert.** Die Conversation-ID lag im React-State;
 *      zwei Speicherungen kurz hintereinander sahen beide `null` und
 *      legten zwei Zeilen an. Hier hält ein Ref die ID, und ein
 *      laufendes `create` wird von Folgeaufrufen abgewartet.
 *   2. **Explizites `reset`/`resume`**, damit „Neues Gespräch" und
 *      „Gespräch fortsetzen" nicht jede Oberfläche selbst nachbaut.
 *
 * Fehler werden bewusst verschluckt: der Verlauf ist Komfort, kein
 * Behandlungsdokument (§630f-relevante Inhalte landen über die Tools in
 * `gncp_entries` & Co., nicht hier). Ein fehlgeschlagener Insert darf
 * das laufende Gespräch nicht abbrechen.
 */

import { useCallback, useRef, useState } from 'react';

import { conversationsRepo } from '@/data';
import type { ChatMessage } from '@/data/conversations';

export interface UseConversationPersistenceArgs {
  orgId: string | null | undefined;
  userId: string | null | undefined;
}

export interface UseConversationPersistence {
  /** ID des laufenden Gesprächs, `null` bevor die erste Zeile existiert. */
  conversationId: string | null;
  /** Schreibt den Stand weg (insert beim ersten Mal, danach update). */
  persist: (messages: ChatMessage[]) => Promise<void>;
  /** An ein bestehendes Gespräch andocken (Verlauf-Sidebar / Auto-Restore). */
  resume: (conversationId: string) => void;
  /** Nächstes `persist` legt eine neue Zeile an. */
  reset: () => void;
}

export function useConversationPersistence({
  orgId,
  userId,
}: UseConversationPersistenceArgs): UseConversationPersistence {
  const [conversationId, setConversationId] = useState<string | null>(null);
  // Spiegelt `conversationId`, ist aber sofort nach dem Insert gesetzt —
  // React-State wäre erst im nächsten Render sichtbar.
  const idRef = useRef<string | null>(null);
  // Hält den laufenden Insert, damit parallele Aufrufe darauf warten
  // statt eine zweite Zeile anzulegen.
  const createRef = useRef<Promise<string> | null>(null);

  const persist = useCallback(
    async (messages: ChatMessage[]): Promise<void> => {
      if (!orgId || !userId) return;
      // Ein einzelner Turn (nur die Begrüßung) ist kein Gespräch.
      if (messages.length <= 1) return;

      try {
        if (!idRef.current) {
          if (!createRef.current) {
            createRef.current = conversationsRepo.createConversation({
              organizationId: orgId,
              userId,
              messages,
            });
            const id = await createRef.current;
            idRef.current = id;
            setConversationId(id);
            return;
          }
          // Ein anderer Aufruf legt die Zeile gerade an — abwarten und
          // den eigenen (neueren) Stand danach als Update nachziehen.
          await createRef.current;
        }
        if (!idRef.current) return;
        await conversationsRepo.updateConversation(idRef.current, userId, messages);
      } catch {
        /* still — siehe Modul-Kommentar */
      }
    },
    [orgId, userId],
  );

  const resume = useCallback((id: string) => {
    createRef.current = null;
    idRef.current = id;
    setConversationId(id);
  }, []);

  const reset = useCallback(() => {
    createRef.current = null;
    idRef.current = null;
    setConversationId(null);
  }, []);

  return { conversationId, persist, resume, reset };
}
